if (!customElements.get('morrow-signup-popup')) {
  customElements.define('morrow-signup-popup', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      if (!this.dialog || !this.form || typeof this.dialog.showModal !== 'function') return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.keys = {
        dismissed: 'morrow-newsletter-dismissed-v1',
        subscribed: 'morrow-newsletter-subscribed-v1',
        pending: 'morrow-newsletter-pending-v1'
      };
      this.designMode = this.dataset.designMode === 'true';
      this.sessionStorageAvailable = this.storageAvailable('sessionStorage');
      document.addEventListener('click', (event) => this.handleClick(event), { signal });
      document.addEventListener('submit', (event) => this.handleSubmit(event), { signal, capture: true });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.dismiss();
      }, { signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dismiss();
      }, { signal });
      this.dialog.addEventListener('close', () => this.restorePage(), { signal });
      document.addEventListener('shopify:section:select', (event) => {
        if (this.designMode && event.detail.sectionId === this.dataset.sectionId) this.requestOpen();
      }, { signal });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (this.designMode && event.detail.sectionId === this.dataset.sectionId) this.dismiss();
      }, { signal });
      if (this.designMode || /\/(?:challenge|password|account|checkouts?)(?:\/|$)/.test(location.pathname)) return;
      if (this.restoreResponse()) return;
      if (location.hash === '#MorrowNewsletter') {
        this.openNewsletter();
        return;
      }
      if (this.dataset.enabled !== 'true' || this.dataset.eligible !== 'true') return;
      if (this.readStorage('sessionStorage', this.keys.dismissed) || this.isSubscribed()) return;
      this.automatic = true;
      this.openWhenReady();
    }

    disconnectedCallback() {
      this.stopWaiting();
      this.abort?.abort();
      if (this.dialog?.open) this.dialog.close();
      this.restorePage();
    }

    readStorage(type, key) {
      try { return window[type].getItem(key); } catch { return null; }
    }

    storageAvailable(type) {
      try {
        const probe = 'morrow-newsletter-storage-check';
        window[type].setItem(probe, '1');
        window[type].removeItem(probe);
        return true;
      } catch { return false; }
    }

    writeStorage(type, key, value) {
      try {
        if (value === null) window[type].removeItem(key);
        else window[type].setItem(key, value);
      } catch {}
    }

    isSubscribed() {
      const at = Number(this.readStorage('localStorage', this.keys.subscribed));
      return this.dataset.knownSubscriber === 'true' || (at > 0 && Date.now() >= at && Date.now() - at < 365 * 24 * 60 * 60 * 1000);
    }

    handleClick(event) {
      const target = event.target instanceof Element ? event.target : event.target.parentElement;
      if (!target) return;
      if (this.contains(target) && target.closest('[data-signup-close]')) {
        this.dismiss();
        return;
      }
      const opener = target.closest('a[href], [data-morrow-signup-open]');
      if (!opener || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (opener.matches('a')) {
        const url = new URL(opener.href, location.href);
        if (url.origin !== location.origin || url.hash !== '#MorrowNewsletter' || opener.target === '_blank' || opener.hasAttribute('download')) return;
      } else if (!opener.hasAttribute('data-morrow-signup-open')) return;
      event.preventDefault();
      this.openNewsletter();
    }

    handleSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('.morrow-newsletter-form')) {
        if (form.querySelector('input[name="form_type"][value="customer"]')) this.writeStorage('sessionStorage', this.keys.pending, null);
        return;
      }
      if (this.designMode) {
        event.preventDefault();
        return;
      }
      if (!form.checkValidity()) {
        event.preventDefault();
        form.reportValidity();
        return;
      }
      this.stopWaiting();
      this.writeStorage('sessionStorage', this.keys.pending, JSON.stringify({ at: Date.now(), formId: form.id }));
    }

    restoreResponse() {
      let pending;
      try { pending = JSON.parse(this.readStorage('sessionStorage', this.keys.pending)); } catch { return false; }
      let fragment;
      try { fragment = decodeURIComponent(location.hash.slice(1)); } catch { return false; }
      if (pending) {
        const age = Date.now() - pending.at;
        if (!Number.isFinite(age) || age < 0 || age > 30 * 60 * 1000) {
          this.writeStorage('sessionStorage', this.keys.pending, null);
          return false;
        }
        if (fragment && fragment !== pending.formId) return false;
      } else if (!this.sessionStorageAvailable && fragment) {
        pending = { formId: fragment };
      } else return false;
      const form = document.getElementById(pending.formId);
      if (!form?.matches('form.morrow-newsletter-form')) return false;
      const state = form.querySelector('[data-signup-state]');
      if (state?.dataset.success === 'true') {
        this.writeStorage('localStorage', this.keys.subscribed, String(Date.now()));
        this.writeStorage('sessionStorage', this.keys.dismissed, '1');
        this.writeStorage('sessionStorage', this.keys.pending, null);
        this.showSuccess();
        this.requestOpen(this.querySelector('[data-signup-success]')); 
        return true;
      }
      if (state?.dataset.errors === 'true') {
        this.writeStorage('sessionStorage', this.keys.pending, null);
        if (form === this.form) {
          const errors = form.querySelector('[data-signup-errors]');
          if (errors) {
            errors.hidden = false;
            const email = form.querySelector('input[type="email"]');
            email?.setAttribute('aria-invalid', 'true');
            email?.setAttribute('aria-describedby', errors.id);
          }
          this.requestOpen(errors);
        }
        return true;
      }
      return false;
    }

    openNewsletter() {
      if (this.isSubscribed()) this.showSuccess();
      this.requestOpen(this.querySelector('[data-signup-success]'));
    }

    showSuccess() {
      const template = this.querySelector('[data-signup-success-template]');
      if (!template) return;
      if (!this.querySelector('[data-signup-success]')) this.form.append(template.content.cloneNode(true));
      const fields = this.querySelector('[data-signup-fields]');
      const description = this.querySelector('.morrow-signup__description');
      if (fields) fields.hidden = true;
      if (description) description.hidden = true;
      this.dialog.removeAttribute('aria-describedby');
    }

    isVisible(element) {
      if (element.closest('[hidden], [aria-hidden="true"]') || !element.getClientRects().length) return false;
      const style = getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse' && style.opacity !== '0';
    }

    hasBlockingOverlay() {
      return [...document.querySelectorAll(
        '#shopify-pc__banner, #shopify-pc__prefs, dialog[open], [role="dialog"], [role="alertdialog"], cart-drawer.active, cart-notification.active, details[open] .menu-drawer, details[open] .search-modal, .product-popup-modal[open]'
      )].some((element) => !this.contains(element) && this.isVisible(element));
    }

    stopWaiting() {
      clearTimeout(this.timer);
      this.timer = null;
      this.safeSince = null;
      this.automatic = false;
    }

    openWhenReady() {
      if (!this.isConnected || this.dialog.open || !this.automatic) return;
      if (this.dataset.enabled !== 'true' || this.dataset.eligible !== 'true' || this.readStorage('sessionStorage', this.keys.dismissed) || this.isSubscribed()) {
        this.stopWaiting();
        return;
      }
      const busy = document.hidden || this.hasBlockingOverlay() || document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
      if (busy) this.safeSince = null;
      else if (this.safeSince === null || this.safeSince === undefined) this.safeSince = Date.now();
      const delay = Math.max(0, Number(this.dataset.delay) || 8000);
      if (!busy && Date.now() - this.safeSince >= delay) {
        this.open();
        return;
      }
      this.timer = setTimeout(() => this.openWhenReady(), 1000);
    }

    requestOpen(focusTarget) {
      this.stopWaiting();
      const requestedAt = Date.now();
      const attempt = () => {
        if (!this.isConnected || Date.now() - requestedAt > 2 * 60 * 1000) return;
        if (document.hidden || this.hasBlockingOverlay()) {
          this.timer = setTimeout(attempt, 500);
          return;
        }
        this.open(focusTarget);
      };
      attempt();
    }

    open(focusTarget) {
      this.stopWaiting();
      if (this.dialog.open) {
        focusTarget?.focus({ preventScroll: true });
        return;
      }
      this.previousFocus = document.activeElement;
      this.previousOverflow = document.documentElement.style.overflow;
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      (focusTarget || this.querySelector('.morrow-signup__title'))?.focus({ preventScroll: true });
    }

    dismiss() {
      this.stopWaiting();
      if (!this.designMode) this.writeStorage('sessionStorage', this.keys.dismissed, '1');
      if (this.dialog.open) this.dialog.close();
    }

    restorePage() {
      if (this.previousOverflow !== undefined) {
        document.documentElement.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
      if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
      this.previousFocus = null;
    }

  });
}
